import {
  GOO_SOURCE_TILES,
  GOO_SPREAD_INTERVAL_SECONDS,
  GOO_ZONE_ID,
  ZONES,
} from '../domain/content.js';
import type { GameState } from '../contract.js';
import { generatedTileAmount, getTileAmount, isPermanentWall, tileKey } from './tiles.js';

const SOURCE_COORDINATES = GOO_SOURCE_TILES.map((key) => {
  const [x, y] = key.split(',').map(Number);
  return { x: x ?? 0, y: y ?? 0 };
});

const GOO_ZONE = ZONES.find((zone) => zone.id === GOO_ZONE_ID);

export function isGooSource(x: number, y: number): boolean {
  return GOO_SOURCE_TILES.includes(tileKey(x, y));
}

export function isGooTile(state: GameState, x: number, y: number): boolean {
  return state.world.gooTiles[tileKey(x, y)] === true;
}

export function cleanGooTile(state: GameState, x: number, y: number): boolean {
  const key = tileKey(x, y);
  if (state.world.gooTiles[key] !== true) return false;
  delete state.world.gooTiles[key];
  state.stats.gooCleaned += 1;
  return true;
}

export function createInitialGooTiles(seed: number): Record<string, true> {
  const gooTiles: Record<string, true> = {};
  for (const source of SOURCE_COORDINATES) {
    for (let dy = -2; dy <= 2; dy++) {
      for (let dx = -2; dx <= 2; dx++) {
        if (Math.abs(dx) + Math.abs(dy) > 2 || (dx === 0 && dy === 0)) continue;
        const x = source.x + dx;
        const y = source.y + dy;
        if (!isInGooZone(x, y) || generatedTileAmount(seed, x, y) > 0) continue;
        gooTiles[tileKey(x, y)] = true;
      }
    }
  }
  return gooTiles;
}

export function updateGoo(state: GameState, dtSec: number): void {
  state.world.gooSpreadElapsedSec += Math.max(0, dtSec);
  while (state.world.gooSpreadElapsedSec >= GOO_SPREAD_INTERVAL_SECONDS) {
    state.world.gooSpreadElapsedSec -= GOO_SPREAD_INTERVAL_SECONDS;
    state.world.gooSpreadStep += 1;
    spreadFromActiveSources(state);
  }
}

function spreadFromActiveSources(state: GameState): void {
  for (let sourceIndex = 0; sourceIndex < SOURCE_COORDINATES.length; sourceIndex++) {
    const source = SOURCE_COORDINATES[sourceIndex];
    if (source === undefined || getTileAmount(state, source.x, source.y) <= 0) continue;
    const candidate = nearestOpenSpillTile(state, source.x, source.y, state.world.gooSpreadStep + sourceIndex);
    if (candidate !== null) state.world.gooTiles[tileKey(candidate.x, candidate.y)] = true;
  }
}

function nearestOpenSpillTile(state: GameState, sourceX: number, sourceY: number, phase: number): { x: number; y: number } | null {
  const maxRadius = GOO_ZONE === undefined ? 0 : GOO_ZONE.width + GOO_ZONE.height;
  for (let radius = 1; radius <= maxRadius; radius++) {
    const candidates: Array<{ x: number; y: number }> = [];
    for (let dy = -radius; dy <= radius; dy++) {
      const dx = radius - Math.abs(dy);
      candidates.push({ x: sourceX + dx, y: sourceY + dy });
      if (dx !== 0) candidates.push({ x: sourceX - dx, y: sourceY + dy });
    }
    const start = candidates.length === 0 ? 0 : phase % candidates.length;
    for (let offset = 0; offset < candidates.length; offset++) {
      const candidate = candidates[(start + offset) % candidates.length];
      if (candidate !== undefined && canReceiveGoo(state, candidate.x, candidate.y)) return candidate;
    }
  }
  return null;
}

function canReceiveGoo(state: GameState, x: number, y: number): boolean {
  if (!isInGooZone(x, y) || isPermanentWall(x, y) || getTileAmount(state, x, y) > 0 || isGooTile(state, x, y)) return false;
  return !state.buildings.some((building) => (
    x >= building.tileX && x < building.tileX + building.width && y >= building.tileY && y < building.tileY + building.height
  ));
}

function isInGooZone(x: number, y: number): boolean {
  return GOO_ZONE !== undefined && x >= GOO_ZONE.minX && x < GOO_ZONE.minX + GOO_ZONE.width && y >= GOO_ZONE.minY && y < GOO_ZONE.minY + GOO_ZONE.height;
}
